import type { Inventory } from '../combat/types';
import type { Hex } from './hex';

export type CharacterId = string;

/**
 * The immutable record stamped on a character when they fall (HEX-65). Its
 * presence on `Character.death` is the alive/dead discriminator: `null` (or
 * absent) means alive. A fallen character keeps its full `inventory`/deck as a
 * world artefact (the foundation for the future ghost/summon mechanic) and
 * surfaces in the graveyard, but never re-enters the active roster.
 */
export interface CharacterDeath {
  /** ms-epoch of the killing blow. */
  readonly at: number;
  /** Hex of the tile where they fell. */
  readonly hex: Hex;
  /** What slew them — the enemy's name. `null` when the cause is unrecorded. */
  readonly cause: string | null;
  /** HP at the moment of death (0 for a combat defeat; frozen for the screen). */
  readonly hp: number;
}

export interface Character {
  readonly id: CharacterId;
  readonly name: string;
  position: Hex;
  /**
   * Per-character "explored" set — hexes this character has personally crossed.
   * Stored as a set of `q,r` keys (see `hexKey`). Distinct from the global discovery
   * registry, which is tile-level metadata.
   */
  explored: Set<string>;
  /**
   * Per-character "revealed" set — hexes a bought map (HEX-73) has shown this
   * character because ANOTHER traveller discovered them. Stored as `q,r` keys
   * (see `hexKey`), persisted like `explored`.
   *
   * REVEAL-NOT-CREDIT: these render for this character (inherited memory face)
   * but are NOT personal discovery — distinct from `explored` (hexes this
   * character actually crossed) and never touch the global first-discovery
   * registry or the `discoveries` leaderboard counter. Sharing knowledge, not
   * faking discovery.
   */
  revealed: Set<string>;
  /**
   * Per-character "seen" set — hexes that have entered this character's vision
   * disk at least once but may never have been crossed (HEX-128). Stored as
   * `q,r` keys (see `hexKey`), persisted like `explored` / `revealed`.
   *
   * REVEAL-NOT-CREDIT: a seen tile renders in memory mode when it leaves vision
   * (so glimpsed terrain stops reverting to black) but is NOT personal discovery
   * — distinct from `explored` (hexes actually crossed) and never touches the
   * global first-discovery registry or the `discoveries` counter. An undiscovered
   * seen tile renders a desaturated `?` remembered-frontier, keeping its content
   * hidden until crossed (HEX-85). A superset of `explored` in practice (you see a
   * tile before you cross it), persisted separately so memory survives reload.
   */
  seen: Set<string>;
  /**
   * Current HP. Carries across combats (HEX-34). Maximum is the class's
   * `baseHp` — the engine constructs `maxHp` from that at `createCombat`
   * time; this field tracks the *post-combat* survivor value persisted
   * between encounters.
   */
  hp: number;
  /**
   * First-cross count, leaderboard-facing (ADR-0012 §7). Incremented by
   * `TileLifecycle.onCross` when `tile.metadata.discoveredBy` transitions
   * from null to this character's id. Never decremented; survives subsequent
   * crosses by the same or other characters.
   */
  discoveries: number;
  /**
   * Placeholder economy currency (HEX-39). Accumulates from combat drops and
   * chest loot; spent at shop tiles. Single scalar — no items, inventory or
   * gear in MVP; refine post-playtest.
   */
  gold: number;
  /**
   * The character's deck-builder collection — a single 3×3 loadout, the only
   * card store (ADR-0022; stash retired), ADR-0015 §1/§18. Persisted per
   * character (HEX-63): card drops and shop purchases place-or-decline into it,
   * the inventory editor rewrites it, and death wipes it with the character
   * (ADR-0015 §7). New characters seed the §5/§19 starter trio; pre-HEX-63
   * characters migrate to the starter on load.
   */
  inventory: Inventory;
  /**
   * Accumulated XP (HEX-117 / ADR-0018 §3). Run-scoped — resets on death.
   * Optional + additive: pre-HEX-117 rows read back as 0 XP, level 1.
   */
  xp?: number;
  /**
   * Current level derived from `xp` (HEX-117 / ADR-0018 §3). Level 1 = 0 XP;
   * increments at XP thresholds via `addXp`. Capped at `XP_MAX_LEVEL`.
   * Optional + additive: pre-HEX-117 rows default to level 1 on load.
   */
  level?: number;
  readonly createdAt: number;
  /**
   * Death record (HEX-65). Absent / `null` while alive — the default for every
   * newly-created character. Set once at combat defeat (`resolveCombatOutcome`),
   * after which the character is archived rather than deleted: excluded from the
   * active roster, retained whole (deck included) as a graveyard entry. Optional
   * so the field is additive — pre-HEX-65 callers and rows read back as alive.
   */
  death?: CharacterDeath | null;
}

/** True when the character has not fallen — the active-roster predicate. */
export function isAlive(character: Character): boolean {
  return character.death == null;
}

const NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 _-]{1,23}$/;

export interface CharacterNameValidation {
  ok: boolean;
  reason?: string;
}

/**
 * Validate a character name for the create-character flow.
 *
 * Rules:
 *   - 2–24 characters after trimming
 *   - First character must be alphanumeric
 *   - Allowed: letters, digits, spaces, `_`, `-`
 *   - No leading/trailing whitespace
 */
export function validateCharacterName(raw: string): CharacterNameValidation {
  if (typeof raw !== 'string') return { ok: false, reason: 'name must be a string' };
  if (raw !== raw.trim()) return { ok: false, reason: 'no leading or trailing whitespace' };
  if (raw.length < 2) return { ok: false, reason: 'at least 2 characters' };
  if (raw.length > 24) return { ok: false, reason: 'at most 24 characters' };
  if (!NAME_PATTERN.test(raw)) {
    return { ok: false, reason: 'letters, digits, spaces, _ or -; must start alphanumeric' };
  }
  return { ok: true };
}
